import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiSave,
  FiDownload,
  FiSearch,
  FiBookOpen,
  FiEdit3,
  FiFileText,
  FiChevronRight,
  FiChevronLeft,
  FiEye,
  FiMaximize,
  FiMinimize,
  FiRefreshCw,
  FiCheck
} from 'react-icons/fi';
import { MdFormatQuote, MdAutoAwesome } from 'react-icons/md';
import { useAssignment } from '../context/AssignmentContext';
import { getFilesFromStorage } from '../utils/fileStorage';
import { toast } from 'sonner';
import styles from '../styles/immersiveAssignment.module.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ImmersiveAssignment = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { groups, activeGroup, updateGroup, setActiveGroup } = useAssignment();
  
  // Editor state
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [selectedText, setSelectedText] = useState('');
  
  // View modes
  const [viewMode, setViewMode] = useState('edit'); // 'edit', 'preview', 'split'
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [citationsOpen, setCitationsOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // References and citations
  const [references, setReferences] = useState([]);
  const [citations, setCitations] = useState([]);
  const [selectedReference, setSelectedReference] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  
  // Editor refs
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const autoSaveTimerRef = useRef(null);

  // Load reference documents
  const loadReferences = useCallback((group) => {
    const files = getFilesFromStorage();
    const refs = [];
    
    if (group.assignmentDocId) {
      const assignmentDoc = files.find(f => f.id === group.assignmentDocId);
      if (assignmentDoc) {
        refs.push({ ...assignmentDoc, role: 'assignment' });
      }
    }
    
    if (group.referenceDocIds && group.referenceDocIds.length > 0) {
      group.referenceDocIds.forEach(refId => {
        const refDoc = files.find(f => f.id === refId);
        if (refDoc) {
          refs.push({ ...refDoc, role: 'reference' });
        }
      });
    }
    
    setReferences(refs);
  }, []);

  // Load assignment data
  useEffect(() => {
    if (groupId) {
      const group = groups.find(g => g.id === groupId);
      if (group) {
        setActiveGroup(groupId);
        setTitle(group.title || '');
        setContent(group.content || '');
        loadReferences(group);
      } else {
        navigate('/resources');
      }
    }
  }, [groupId, groups, setActiveGroup, navigate, loadReferences]);

  // Save content
  const saveContent = useCallback(async (isAutoSave = false) => {
    if (!groupId) return;
    
    setIsSaving(true);
    try {
      updateGroup(groupId, { 
        content, 
        title,
        lastModified: new Date().toISOString()
      });
      
      setLastSaved(new Date());
      
      if (!isAutoSave) {
        toast.success('Assignment saved successfully');
      }
    } catch (error) {
      toast.error('Failed to save assignment');
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  }, [groupId, content, title, updateGroup]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      if (content && groupId) {
        saveContent(true);
      }
    }, 2000); // Auto-save after 2 seconds of inactivity
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [content, groupId, saveContent]);

  // Word count
  useEffect(() => {
    const words = content.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [content]);

  // Generate assignment with AI
  const generateAssignment = useCallback(async () => {
    if (!activeGroup || references.length === 0) {
      toast.error('Please add assignment and reference documents first');
      return;
    }
    
    setIsGenerating(true);
    setGenerationProgress(0);
    
    try {
      // Prepare document data
      const formData = new FormData();
      
      // Add assignment document
      const assignmentDoc = references.find(r => r.role === 'assignment');
      if (assignmentDoc && assignmentDoc.content) {
        formData.append('assignment_doc', new Blob([assignmentDoc.content], { type: 'text/plain' }), assignmentDoc.name);
      }
      
      // Add reference documents
      const referenceDocs = references.filter(r => r.role === 'reference');
      referenceDocs.forEach((doc, index) => {
        if (doc.content) {
          formData.append(`reference_${index}`, new Blob([doc.content], { type: 'text/plain' }), doc.name);
        }
      });
      
      // Add generation parameters
      formData.append('mode', activeGroup.settings?.mode || 'essay');
      formData.append('auto_cite', activeGroup.settings?.autoCite || true);
      formData.append('title', title || 'Untitled Assignment');
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 10, 90));
      }, 500);
      
      const response = await fetch(`${API_BASE}/api/generate_assignment`, {
        method: 'POST',
        body: formData
      });
      
      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      if (!response.ok) {
        throw new Error('Failed to generate assignment');
      }
      
      const result = await response.json();
      
      if (result.success) {
        setContent(result.content);
        setCitations(result.citations || []);
        toast.success('Assignment generated successfully!');
      } else {
        throw new Error(result.error || 'Generation failed');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate assignment: ' + error.message);
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  }, [activeGroup, references, title]);

  // Extract and add citation
  const addCitation = useCallback(async () => {
    if (!selectedText || !selectedReference) {
      toast.error('Please select text and a reference document');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/extract_citation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quote: selectedText,
          source: selectedReference.name,
          sourceId: selectedReference.id,
          context: content
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        const citation = result.citation;
        setCitations(prev => [...prev, citation]);
        
        // Insert citation in text
        const citationText = `"${selectedText}" [${citations.length + 1}]`;
        const newContent = content.replace(selectedText, citationText);
        setContent(newContent);
        
        toast.success('Citation added successfully');
      }
    } catch (error) {
      console.error('Citation error:', error);
      toast.error('Failed to add citation');
    }
  }, [selectedText, selectedReference, content, citations]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Export assignment
  const exportAssignment = useCallback(() => {
    const exportContent = `# ${title}\n\n${content}\n\n## References\n\n${citations.map((c, i) => 
      `[${i + 1}] ${c.source}: "${c.quote}" (Page ${c.page || 'N/A'})`
    ).join('\n')}`;
    
    const blob = new Blob([exportContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || 'assignment'}.md`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Assignment exported successfully');
  }, [title, content, citations]);

  return (
    <div ref={containerRef} className={styles.immersiveContainer}>
      {/* Header Bar */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            className={styles.backButton}
            onClick={() => navigate('/resources')}
            title="Back to Resources"
          >
            <FiArrowLeft />
          </button>
          
          <div className={styles.titleSection}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Assignment Title..."
              className={styles.titleInput}
            />
            {lastSaved && (
              <span className={styles.saveStatus}>
                <FiCheck className={styles.savedIcon} />
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.stats}>
            <span className={styles.wordCount}>
              {wordCount} words
            </span>
            <span className={styles.citationCount}>
              <MdFormatQuote />
              {citations.length} citations
            </span>
          </div>

          <div className={styles.viewModes}>
            <button
              className={`${styles.viewMode} ${viewMode === 'edit' ? styles.active : ''}`}
              onClick={() => setViewMode('edit')}
              title="Edit Mode"
            >
              <FiEdit3 />
            </button>
            <button
              className={`${styles.viewMode} ${viewMode === 'preview' ? styles.active : ''}`}
              onClick={() => setViewMode('preview')}
              title="Preview Mode"
            >
              <FiEye />
            </button>
            <button
              className={`${styles.viewMode} ${viewMode === 'split' ? styles.active : ''}`}
              onClick={() => setViewMode('split')}
              title="Split View"
            >
              <FiChevronRight />
              <FiChevronLeft />
            </button>
          </div>

          <div className={styles.actions}>
            <button
              className={styles.actionButton}
              onClick={generateAssignment}
              disabled={isGenerating}
              title="Generate with AI"
            >
              {isGenerating ? <FiRefreshCw className="spin" /> : <MdAutoAwesome />}
              {isGenerating ? 'Generating...' : 'Generate'}
            </button>
            <button
              className={styles.actionButton}
              onClick={() => saveContent(false)}
              disabled={isSaving}
              title="Save"
            >
              <FiSave />
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              className={styles.iconButton}
              onClick={exportAssignment}
              title="Export"
            >
              <FiDownload />
            </button>
            <button
              className={styles.iconButton}
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <FiMinimize /> : <FiMaximize />}
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {isGenerating && (
        <div className={styles.progressBar}>
          <div 
            className={styles.progressFill} 
            style={{ width: `${generationProgress}%` }}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Sidebar - References */}
        {sidebarOpen && (
          <div className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <h3>
                <FiBookOpen />
                References
              </h3>
              <button
                className={styles.collapseButton}
                onClick={() => setSidebarOpen(false)}
                title="Hide Sidebar"
              >
                <FiChevronLeft />
              </button>
            </div>

            <div className={styles.searchBox}>
              <FiSearch />
              <input
                type="text"
                placeholder="Search references..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className={styles.referencesList}>
              {references.filter(ref => 
                ref.name.toLowerCase().includes(searchQuery.toLowerCase())
              ).map((ref) => (
                <div
                  key={ref.id}
                  className={`${styles.referenceItem} ${
                    selectedReference?.id === ref.id ? styles.selected : ''
                  } ${ref.role === 'assignment' ? styles.assignment : ''}`}
                  onClick={() => setSelectedReference(ref)}
                >
                  <FiFileText />
                  <div className={styles.refInfo}>
                    <span className={styles.refName}>{ref.name}</span>
                    <span className={styles.refRole}>
                      {ref.role === 'assignment' ? 'Assignment' : 'Reference'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {selectedReference && (
              <div className={styles.referencePreview}>
                <h4>{selectedReference.name}</h4>
                <div className={styles.previewContent}>
                  {selectedReference.content ? 
                    selectedReference.content.substring(0, 200) + '...' : 
                    'Loading content...'
                  }
                </div>
                <button
                  className={styles.citeButton}
                  onClick={addCitation}
                  disabled={!selectedText}
                >
                  <MdFormatQuote />
                  Add Citation
                </button>
              </div>
            )}
          </div>
        )}

        {/* Editor Area */}
        <div className={styles.editorContainer}>
          {!sidebarOpen && (
            <button
              className={styles.sidebarToggle}
              onClick={() => setSidebarOpen(true)}
              title="Show Sidebar"
            >
              <FiChevronRight />
            </button>
          )}

          <div className={`${styles.editorWrapper} ${styles[viewMode]}`}>
            {(viewMode === 'edit' || viewMode === 'split') && (
              <div className={styles.editor}>
                <textarea
                  ref={editorRef}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onSelect={(e) => {
                    const text = e.target.value.substring(
                      e.target.selectionStart,
                      e.target.selectionEnd
                    );
                    setSelectedText(text);
                  }}
                  placeholder="Start writing your assignment here... You can also click 'Generate' to let AI create a draft based on your documents."
                  className={styles.editorTextarea}
                />
              </div>
            )}

            {(viewMode === 'preview' || viewMode === 'split') && (
              <div className={styles.preview}>
                <div 
                  className={styles.previewContent}
                  dangerouslySetInnerHTML={{
                    __html: content
                      .replace(/\n\n/g, '</p><p>')
                      .replace(/^/, '<p>')
                      .replace(/$/, '</p>')
                      .replace(/\[(\d+)\]/g, '<sup class="citation">[$1]</sup>')
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Citations Panel */}
        {citationsOpen && (
          <div className={styles.citationsPanel}>
            <div className={styles.citationsHeader}>
              <h3>
                <MdFormatQuote />
                Citations
              </h3>
              <button
                className={styles.collapseButton}
                onClick={() => setCitationsOpen(false)}
                title="Hide Citations"
              >
                <FiChevronRight />
              </button>
            </div>

            <div className={styles.citationsList}>
              {citations.length === 0 ? (
                <div className={styles.noCitations}>
                  <MdFormatQuote />
                  <p>No citations yet</p>
                  <small>Select text and click "Add Citation" to add references</small>
                </div>
              ) : (
                citations.map((citation, index) => (
                  <div key={index} className={styles.citationItem}>
                    <span className={styles.citationNumber}>[{index + 1}]</span>
                    <div className={styles.citationContent}>
                      <p className={styles.citationQuote}>"{citation.quote}"</p>
                      <p className={styles.citationSource}>
                        {citation.source}
                        {citation.page && ` (p. ${citation.page})`}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {!citationsOpen && (
          <button
            className={styles.citationsToggle}
            onClick={() => setCitationsOpen(true)}
            title="Show Citations"
          >
            <MdFormatQuote />
            {citations.length}
          </button>
        )}
      </div>
    </div>
  );
};

export default ImmersiveAssignment;
